import { createContext, useContext, ReactNode } from 'react';
import {
  Auth0Provider,
  useAuth0,
  RedirectLoginOptions,
  LogoutOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-react';
import { User as Auth0User } from '@auth0/auth0-spa-js';

interface Auth0SafeState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: Auth0User | undefined;
  getAccessTokenSilently: (
    options?: GetTokenSilentlyOptions,
  ) => Promise<string>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
}

export class Auth0NotConfiguredError extends Error {
  constructor() {
    super(
      'Auth0 is not configured. Set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.',
    );
    this.name = 'Auth0NotConfiguredError';
  }
}

const defaultState: Auth0SafeState = {
  isAuthenticated: false,
  isLoading: false,
  user: undefined,
  getAccessTokenSilently: async () => '',
  loginWithRedirect: async () => {
    throw new Auth0NotConfiguredError();
  },
  logout: async () => {},
};

const Auth0SafeContext = createContext<Auth0SafeState>(defaultState);

/**
 * Bridge component that reads the real Auth0 state and forwards it
 * through our own context so consumers can use useAuth0Safe() instead
 * of useAuth0() — avoiding the "must be inside Auth0Provider" error.
 */
const Auth0StateProxy = ({ children }: { children: ReactNode }) => {
  const auth0 = useAuth0();
  return (
    <Auth0SafeContext.Provider value={auth0}>
      {children}
    </Auth0SafeContext.Provider>
  );
};

/**
 * Wraps children with Auth0Provider when configured (VITE_AUTH0_DOMAIN + VITE_AUTH0_CLIENT_ID).
 * When not configured, provides a no-op Auth0 state so all existing code can
 * call useAuth0Safe() without errors.
 */
export const Auth0Wrapper = ({ children }: { children: ReactNode }) => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  if (!domain || !clientId) {
    return (
      <Auth0SafeContext.Provider value={defaultState}>
        {children}
      </Auth0SafeContext.Provider>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience,
      }}
      cacheLocation='localstorage'
      useRefreshTokens={true}
    >
      <Auth0StateProxy>{children}</Auth0StateProxy>
    </Auth0Provider>
  );
};

/**
 * Safe hook to access Auth0 state. Always works, whether or not Auth0Provider
 * is in the component tree. Returns inactive defaults when Auth0 is not configured.
 */
export const useAuth0Safe = () => useContext(Auth0SafeContext);
